import { ElementProps } from "@/common/contracts/input.contracts";
import { cn } from "@/common/utils/css.utils";
import { cva, VariantProps } from "class-variance-authority";
import {
  ComponentProps,
  forwardRef,
  isValidElement,
  ReactNode,
  Ref,
} from "react";

const TextAreaStyle = cva(
  "peer flex-1 rounded-[4px] border bg-white px-4 py-2.5 font-body text-sm text-black placeholder:text-grey-500 focus:outline-none focus:ring-2 focus:ring-curious-200 disabled:cursor-not-allowed disabled:border-grey-400 disabled:bg-grey-50 disabled:text-grey-600 disabled:placeholder:text-grey-300",
  {
    variants: {
      color: {
        brand: "",
        curious: "",
      },
      valid: {
        true: "",
        false: "",
      },
      disabled: {
        true: "",
        false: "",
      },
    },
    compoundVariants: [
      /* Valid, not disabled */
      {
        valid: true,
        disabled: false,
        className: "border-grey-500",
      },
      /* Valid, not disabled, brand */
      {
        valid: true,
        disabled: false,
        color: "brand",
        className: "hover:border-brand-500 focus:border-brand-500",
      },
      /* Valid, not disabled, curious */
      {
        valid: true,
        disabled: false,
        color: "curious",
        className: "hover:border-curious-500 focus:border-curious-500",
      },
      /* Not valid, not disabled */
      {
        valid: false,
        disabled: false,
        className: "border-attention-500 focus:border-attention-500",
      },
    ],
    defaultVariants: {
      color: "curious",
      valid: true,
      disabled: false,
    },
  }
);

type TextAreaPropsBase = {
  ref?: Ref<HTMLTextAreaElement>;
  inputRef?: Ref<HTMLTextAreaElement>;
  valid?: boolean;
  placeholder?: ReactNode | ((props: ElementProps) => ReactNode);
} & VariantProps<typeof TextAreaStyle> &
  Omit<ComponentProps<"textarea">, "ref" | "color" | "placeholder">;

const TextAreaBase = ({
  inputRef,
  color,
  valid,
  disabled,
  placeholder,
  className,
  ...props
}: TextAreaPropsBase) => {
  const placeholderProps = {
    /**
     * If we are passing React component as a placeholder (in most cases {@link TextAreaAnimatedPlaceholder})
     * then we need to make sure to set the placeholder to a single space for animated events to take place.
     */
    placeholder: typeof placeholder === "string" ? placeholder : " ",
  };

  const renderPlaceholder = () => {
    if (typeof placeholder === "function") {
      return placeholder({ valid, disabled });
    }

    if (isValidElement(placeholder)) {
      return placeholder;
    }
  };

  return (
    <div className="relative flex">
      <textarea
        ref={inputRef}
        {...props}
        {...placeholderProps}
        disabled={disabled}
        // Merged, not appended: a caller's text-base or height replaces the defaults (text-sm)
        // instead of competing with them in the stylesheet's order.
        className={cn(TextAreaStyle({ color, valid, disabled }), className)}
      />
      {renderPlaceholder()}
    </div>
  );
};

TextAreaBase.displayName = "TextArea";

export type TextAreProps = Omit<TextAreaPropsBase, "inputRef">;

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreProps>(
  (props, ref) => <TextAreaBase {...props} inputRef={ref} />
);
